import { Box, Button, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getDatosCIUOPuesto } from "../../../../../redux/actions/resolucion";
import FormikTextField from "../../../../commons/formik/FormikTextField";
import CharactersCounterLabel from "../../../../commons/labels/CharactersCounterLabel";
import RequeridoLabel from '../../../../commons/labels/RequeridoLabel';
import PuestosFormContainer from "../../../../commons/puestos/PuestosFormContainer";
import TabsWithErrors, { TabPanel } from "../../../../commons/tab/tabWithError/TabsWithErrors";
import { tableConfig } from "../../../../commons/table/tableConfig";
import { StyledResolucion } from "../../../../styled/Prevencion";
import ElementosDeProteccionTabContainer from './elementosDeProteccion/ElementosDeProteccionTabContainer';
import MedidasPreventivasTabContainer from "./medidasPreventivas/MedidasPreventivasTabContainer";
import { puestosAsociadosValidation } from "./puestosValidations";

const SustanciasSelectionTable = ({ sustancias, selectedIds, onChange }) => {

  const tableData = useMemo(() =>
    sustancias.map((s) => ({
      id: s.id,
      codigo: s.sustancia?.nombreAgente ?? "",
      nombreComercial: s.sustancia?.nombre ?? "",
      descripcion: s.sustancia?.descripcionAgente ?? "",
    })),
    [sustancias]
  );

  // Use sustancia id as the MRT row id so selection maps by id, not by index
  const getRowId = (row) => String(row.id);

  // Build MRT selection object: { [sustanciaId]: true }
  const rowSelection = useMemo(() => {
    const sel = {};
    selectedIds.forEach((id) => {
      sel[String(id)] = true;
    });
    return sel;
  }, [selectedIds]);

  const columns = useMemo(() => [
    {
      accessorKey: "codigo",
      header: "Código",
      size: 120,
    },
    {
      accessorKey: "nombreComercial",
      header: "Nombre Comercial",
      size: 180,
    },
    {
      accessorKey: "descripcion",
      header: "Descripción de Sustancia",
      size: 250,
    },
  ], []);

  const handleRowSelectionChange = (updater) => {
    const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
    const newSelectedIds = Object.keys(newSelection)
      .filter((key) => newSelection[key])
      .map((key) => {
        const found = sustancias.find((s) => String(s.id) === key);
        return found?.id;
      })
      .filter((id) => id !== undefined);
    onChange(newSelectedIds);
  };

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: tableData,
    getRowId,
    enableRowSelection: true,
    onRowSelectionChange: handleRowSelectionChange,
    state: { rowSelection },
    enablePagination: tableData.length > 10,
    enableColumnActions: false,
    enableSorting: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableGlobalFilter: false,
    enableFilters: false,
    enableColumnFilters: false,
    enableTopToolbar: true,
    enableBottomToolbar: tableData.length > 10,
    renderTopToolbarCustomActions: () => (
      <StyledResolucion.HeaderContainer>
        <StyledResolucion.TableTitle>
          Sustancias Asociadas al Puesto
        </StyledResolucion.TableTitle>
      </StyledResolucion.HeaderContainer>
    ),
    renderToolbarAlertBannerContent: ({ selectedAlert }) => (
      <Box sx={{ display: "flex", marginLeft: 1 }}>
        {selectedAlert}
      </Box>
    ),
    localization: {
      ...MRT_Localization_ES,
      noRecordsToDisplay: "No hay sustancias registradas. Cargue sustancias en la Pantalla 3.",
      selectedCountOfRowCountRowsSelected: '{selectedCount} de {rowCount} sustancia(s) seleccionada(s)',
    },
    initialState: {
      density: 'compact',
    },
  });

  return <MaterialReactTable table={table} />;
};

const PuestosAsociadosForm = ({
  initialValues,
  onSubmit,
  onCancel,
  sustancias,
  existingPuestos,
  editingIndex,
}) => {
  const dispatch = useDispatch();
  const ref = useRef();
  const [tabIndex, setTabIndex] = useState(0);
  const idDependenciaEmpleador = useSelector(
    (state) => state.resolucion.searchedEstablecimiento
  );

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={puestosAsociadosValidation(existingPuestos, editingIndex)}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, errors, touched, submitCount }) => (

        <Form >
          <Typography ref={ref} variant="h6" style={{ paddingLeft: "20px" }}>
            Puesto Afectado
          </Typography>
          <div style={{ padding: "10px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PuestosFormContainer
                  name="puesto"
                  add
                  idDependenciaEmpleador={idDependenciaEmpleador}
                  callBack={(e) => {
                    setFieldValue('nombrePuesto', e?.nombre ?? "");
                    if (e?.id) {
                      dispatch(getDatosCIUOPuesto(e.id)).then((res) => {
                        setFieldValue(
                          "descripcionActividades",
                          res.value.data.descripcion ?? ""
                        );
                      })
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Tabla de Sustancias Asociadas al Puesto */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <SustanciasSelectionTable
                sustancias={sustancias}
                selectedIds={values.sustanciasSeleccionadas || []}
                onChange={(ids) => setFieldValue('sustanciasSeleccionadas', ids)}
              />
              {errors.sustanciasSeleccionadas && (touched.sustanciasSeleccionadas || submitCount > 0) && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.sustanciasSeleccionadas}
                </Typography>
              )}
            </Box>

            {/* Campos de texto con padding */}
            <Grid container spacing={3} sx={{ marginTop: 2 }}>
              {" "}
              {/* Espaciado de grillas reducido */}
              <Grid item xs={12} md={6}>
                <FormikTextField
                  name="descripcionEstudios"
                  label="Estudios y posibilidades de reemplazo de la sustancia"
                  multiline
                  maxRows={15}
                  minRows={2}
                  fullWidth
                  filled
                />
                <CharactersCounterLabel
                  value={values.descripcionEstudios}
                  maxCharacters={150}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormikTextField
                  name="descripcionActividades"
                  label="Descripción de Actividades en el Puesto Afectado"
                  multiline
                  maxRows={15}
                  minRows={2}
                  fullWidth
                  filled
                />
                <CharactersCounterLabel
                  value={values.descripcionActividades}
                  maxCharacters={300}
                />
              </Grid>
              <Grid container item xs={12} sx={{ justifyContent: "center", alignItems: "center" }} >
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.informaRiesgos}
                        onChange={() =>
                          setFieldValue(
                            "informaRiesgos",
                            !values.informaRiesgos
                          )
                        }
                      />
                    }
                    label="Informa sobre los riesgos a los empleados"
                  />
                </Grid>
                <Grid item xs="3">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.poseeCapacitaciones}
                        onChange={() =>
                          setFieldValue(
                            "poseeCapacitaciones",
                            !values.poseeCapacitaciones
                          )
                        }
                      />
                    }
                    label="Posee Capacitaciones sobre los riesgos"
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} sx={{ justifyContent: "center", alignItems: "center" }} >
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.entregaProteccion}
                        onChange={() =>
                          setFieldValue(
                            "entregaProteccion",
                            !values.entregaProteccion
                          )
                        }
                      />
                    }
                    label="La empresa entrega elementos de protección"
                  />
                </Grid>
                <Grid item xs="3">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.licenciasEspeciales}
                        onChange={() =>
                          setFieldValue(
                            "licenciasEspeciales",
                            !values.licenciasEspeciales
                          )
                        }
                      />
                    }
                    label="La empresa otorga licencias especiales"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} >
                <RequeridoLabel text="Complete los datos de protección o medidas preventivas si corresponde. De lo contrario, presione guardar"
                  labelContainerStyle={{ margin: 8 }} />
              </Grid>
              <Grid item xs={2}>
                <TabsWithErrors
                  handleTabChange={handleTabChange}
                  tabIndex={tabIndex}
                  tabs={[
                    { label: "El. DE PROTECCIÓN", id: "elementosDeProteccion" },
                    { label: "MEDIDAS PREVENTIVAS", id: "medidasPreventivas" },
                  ]}
                />
              </Grid>
              <Grid item xs={10}>
                <TabPanel value={tabIndex} index={0}>
                  <ElementosDeProteccionTabContainer />
                </TabPanel>
                <TabPanel value={tabIndex} index={1}>
                  <MedidasPreventivasTabContainer />
                </TabPanel>
              </Grid>
            </Grid>

            <div
              style={{
                margin: "10px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={onCancel}
                style={{ marginRight: "10px" }}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="contained">
                Guardar
              </Button>
            </div>
          </div>
        </Form >

      )
      }
    </Formik >
  );
};

export default PuestosAsociadosForm;
